import { Link } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import { useAuth } from '../../hooks/useAuth';
import { Header } from '../../components/layout/Header';

export default function CartPage() {
  const { items, updateQuantity, removeItem, clearCart, getTotalPrice, getTotalItems } = useCartStore();

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  if (items.length === 0) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-4xl mx-auto px-4">
            <h1 className="text-3xl font-bold mb-8">장바구니</h1>
            <div className="bg-white rounded-lg p-12 text-center shadow-md">
              <i className="ri-shopping-cart-line text-6xl text-gray-400 mb-4"></i>
              <h2 className="text-xl font-semibold text-gray-600 mb-4">장바구니가 비어있습니다</h2>
              <p className="text-gray-500 mb-8">원하는 상품을 장바구니에 담아보세요!</p>
              <Link
                to="/products"
                className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                상품 둘러보기
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold">장바구니</h1>
            <button
              onClick={clearCart}
              className="text-red-600 hover:text-red-700 font-medium cursor-pointer"
            >
              <i className="ri-delete-bin-line mr-1"></i>
              전체 삭제
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* 장바구니 아이템 */}
            <div className="lg:col-span-2 space-y-4">
              {items.map((item) => (
                <div key={item.product.id} className="bg-white rounded-lg p-6 shadow-md">
                  <div className="flex items-center space-x-4">
                    <Link to={`/product/${item.product.id}`}>
                      <img
                        src={item.product.image_url}
                        alt={item.product.name}
                        className="w-20 h-20 object-cover object-top rounded-lg"
                      />
                    </Link>
                    
                    <div className="flex-1">
                      <Link 
                        to={`/product/${item.product.id}`}
                        className="font-semibold text-gray-800 hover:text-teal-600 transition-colors"
                      >
                        {item.product.name}
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">{item.product.category}</p>
                      <p className="text-lg font-bold text-teal-600 mt-2">
                        {item.product.price.toLocaleString()}원
                      </p>
                    </div>

                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-12 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                        disabled={item.quantity >= item.product.stock_quantity}
                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>

                    <div className="text-right">
                      <p className="font-bold text-lg">
                        {(item.product.price * item.quantity).toLocaleString()}원
                      </p>
                      <button
                        onClick={() => removeItem(item.product.id)}
                        className="text-red-600 hover:text-red-700 text-sm mt-2 cursor-pointer"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 주문 요약 */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg p-6 shadow-md sticky top-8">
                <h2 className="text-xl font-semibold mb-6">주문 요약</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">상품 개수</span>
                    <span>{getTotalItems()}개</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">상품 금액</span>
                    <span>{getTotalPrice().toLocaleString()}원</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">배송비</span>
                    <span className="text-green-600">무료</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-lg font-bold">
                      <span>총 결제 금액</span>
                      <span className="text-teal-600">{getTotalPrice().toLocaleString()}원</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Link
                    to="/checkout"
                    className="block w-full bg-teal-600 hover:bg-teal-700 text-white py-3 rounded-lg font-semibold text-center transition-colors whitespace-nowrap"
                  >
                    주문하기
                  </Link>
                  <Link
                    to="/products"
                    className="block w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-lg font-semibold text-center transition-colors whitespace-nowrap"
                  >
                    쇼핑 계속하기
                  </Link>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-shield-check-line text-green-600 mr-2"></i>
                    <span>안전한 결제 보장</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600 mt-2">
                    <i className="ri-truck-line text-blue-600 mr-2"></i>
                    <span>무료 배송 (전국)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
